<template>
  <div class="lego-circular-progress" :style="containerStyle">
    <div class="progress-circle" :style="circleStyle">
      <svg
        class="progress-svg"
        :width="size"
        :height="size"
        viewBox="0 0 100 100"
      >
        <!-- Círculo de fondo -->
        <circle
          class="progress-bg"
          cx="50"
          cy="50"
          :r="radius"
          :stroke-width="strokeWidth"
          fill="none"
        />
        <!-- Círculo de progreso -->
        <circle
          class="progress-fill"
          cx="50"
          cy="50"
          :r="radius"
          :stroke-width="strokeWidth"
          fill="none"
          :stroke-dasharray="circumference"
          :stroke-dashoffset="dashOffset"
          :style="{ stroke: progressColor }"
        />
      </svg>
      <!-- Contenido central -->
      <div class="progress-content">
        <div class="progress-value">
          {{ displayValue }}
        </div>
        <div v-if="showPercentage" class="progress-percentage">%</div>
      </div>
    </div>
    <!-- Información adicional debajo del círculo -->
    <div v-if="additionalInfo" class="additional-info">
      <div class="info-line">{{ additionalInfo.primary }}</div>
      <div class="info-line secondary">{{ additionalInfo.secondary }}</div>
    </div>
  </div>
</template>

<script>
export default {
  name: "LegoCircularProgress",
  props: {
    value: {
      type: Number,
      default: 0,
      validator: (value) => value >= 0 && value <= 100,
    },
    size: {
      type: Number,
      default: 35,
    },
    strokeWidth: {
      type: Number,
      default: 16,
    },
    showPercentage: {
      type: Boolean,
      default: false,
    },
    colorScheme: {
      type: String,
      default: "auto", // auto, blue, yellow, red, green
      validator: (value) =>
        ["auto", "blue", "yellow", "red", "green"].includes(value),
    },
    customColor: {
      type: String,
      default: null,
    },
    additionalInfo: {
      type: Object,
      default: null,
      validator: (value) => {
        if (!value) return true;
        return value.primary && value.secondary;
      },
    },
  },
  computed: {
    radius() {
      return 50 - this.strokeWidth / 2;
    },
    circumference() {
      return 2 * Math.PI * this.radius;
    },
    dashOffset() {
      const progress = Math.min(Math.max(this.value, 0), 100);
      return this.circumference - (progress / 100) * this.circumference;
    },
    displayValue() {
      return Math.round(this.value);
    },
    progressColor() {
      if (this.customColor) {
        return this.customColor;
      }

      if (this.colorScheme !== "auto") {
        return this.getColorByScheme(this.colorScheme);
      }

      return this.getColorByValue(this.value);
    },
    containerStyle() {
      return {
        width: `${this.size}px`,
        height: this.additionalInfo ? `${this.size + 40}px` : `${this.size}px`,
      };
    },
    circleStyle() {
      return {
        width: `${this.size}px`,
        height: `${this.size}px`,
      };
    },
  },
  methods: {
    getColorByValue(value) {
      const val = Math.round(value);
      const colorInc = 100 / 3;
      if (val < colorInc) return "#d63031"; // Rojo para valores bajos
      if (val < colorInc * 2) return "#fdcb6e"; // Amarillo para valores medios
      return "#00b894"; // Verde para valores altos
    },
    getColorByScheme(scheme) {
      const colors = {
        red: "#d63031",
        yellow: "#fdcb6e",
        green: "#00b894",
        blue: "#2563EB",
      };
      return colors[scheme] || colors.red;
    },
  },
};
</script>

<style scoped>
.lego-circular-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.progress-circle {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-svg {
  position: absolute;
  top: 0;
  left: 0;
  transform: rotate(-90deg);
}

.progress-bg {
  stroke: #e5e7eb;
  opacity: 0.3;
}

.progress-fill {
  transition: stroke-dashoffset 0.6s ease-in-out;
  stroke-linecap: round;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.progress-content {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  z-index: 1;
}

.progress-value {
  font-weight: 800;
  font-size: 1.1em;
  line-height: 1;
  margin: 0;
  color: #6b7280;
}

.progress-percentage {
  font-size: 0.6em;
  font-weight: 500;
  opacity: 0.8;
  margin-top: -2px;
}

.additional-info {
  margin-top: 8px;
  text-align: center;
  font-size: 0.7em;
  line-height: 1.2;
}

.info-line {
  color: #6b7280;
  font-weight: 500;
}

.info-line.secondary {
  color: #9ca3af;
  font-weight: 400;
  margin-top: 1px;
}

/* Dark mode support */
.dark .progress-bg {
  stroke: #374151;
}

.dark .progress-value {
  color: #d1d5db;
}

.dark .info-line {
  color: #d1d5db;
}

.dark .info-line.secondary {
  color: #9ca3af;
}
</style>
